/**
 * PlantID AI Canonical Botanical Image Database & Verification Engine (Cleaned - No Unsplash)
 */

export const CANONICAL_PLANT_IMAGES: Record<string, string> = {};

export const FALLBACK_TOPIC_IMAGES = {
  plant: "",
  indoor: "",
  disease: "",
  gardening: "",
  succulent: "",
  identification: "",
  watering: "",
  monstera: ""
};

export const CANONICAL_BLOG_IMAGES: Record<string, string> = {};

export const CATEGORY_IMAGES: Record<string, string> = {
  "Plant Care": "",
  "Plant Diseases": "",
  "Indoor Plants": "",
  "Gardening": "",
  "Succulents": "",
  "Flowers": "",
  "Trees": "",
  "Herbs": "",
  "Diagnostics": "",
  "Soil & Water": ""
};

export const KEYWORD_IMAGES: Record<string, string> = {};

export const CATEGORY_FALLBACKS_LOCAL: Record<string, string> = {
  "Plant Care": "",
  "Plant Diseases": "",
  "Indoor Plants": "",
  "Gardening": "",
  "Succulents": "",
  "Flowers": "",
  "Trees": "",
  "Herbs": ""
};

export function getBlogCategoryFallback(category: string): string {
  return "";
}

export function compressImageToDataURL(file: File, maxDimension: number = 1024, quality: number = 0.75): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const blobUrl = URL.createObjectURL(file);
    const img = new Image();
    img.src = blobUrl;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions keeping aspect ratio
      if (width > height) {
        if (width > maxDimension) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        }
      } else {
        if (height > maxDimension) {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve(dataUrl);
      } else {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target?.result as string);
        reader.onerror = (err) => reject(err);
        reader.readAsDataURL(file);
      }
      URL.revokeObjectURL(blobUrl);
    };
    img.onerror = (err) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target?.result as string);
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
      URL.revokeObjectURL(blobUrl);
    };
  });
}

export function getBlogImage(article: any): string {
  const defaultFallback = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 500" fill="none"><rect width="800" height="500" fill="%23ECFDF5"/><path d="M400 100 C250 100 200 250 200 350 C350 350 500 300 500 150 Z" fill="%2310B981" opacity="0.85"/><path d="M400 100 C450 200 450 350 200 350 C300 380 500 350 600 200 Z" fill="%23059669" opacity="0.95"/><path d="M400 350 L400 450" stroke="%23047857" stroke-width="8" stroke-linecap="round"/><circle cx="400" cy="180" r="100" stroke="%2334D399" stroke-width="2" stroke-dasharray="10 15"/></svg>`;

  if (!article) return defaultFallback;
  if (typeof article === "string") {
    article = { title: article };
  }
  let explicitImage = article.featureImage || article.featuredImage || article.featured_image || article.image || article.ogImage;
  if (explicitImage && typeof explicitImage === "string" && explicitImage.trim() !== "") {
    explicitImage = explicitImage.trim();
    // Prepend data URL prefix if it looks like a raw base64 string
    if (!explicitImage.startsWith("http") && !explicitImage.startsWith("data:") && !explicitImage.startsWith("/") && explicitImage.length > 80) {
      return `data:image/jpeg;base64,${explicitImage}`;
    }
    return explicitImage;
  }
  
  return defaultFallback;
}

export const VERIFIED_BOTANICAL_IMAGES: Record<string, string> = {};

export const BOTANICAL_PLACEHOLDER_IMAGE = "";

export function getCorrectPlantImage(commonName: string, scientificName?: string): string {
  return "";
}

export function getVerifiedSpeciesGallery(scientificName: string, commonName: string): string[] {
  return ["", "", "", ""];
}

export function getCorrectBlogImage(title: string, category?: string): string {
  return getBlogImage({ title, category });
}

// Check helper to filter out any empty or invalid URLs
export function isSafeAdminImage(url: string | null | undefined): boolean {
  if (!url) return false;
  const lowerUrl = url.toLowerCase().trim();
  // Admin-uploaded images are either data:image (Base64), raw Base64, clean local paths, or any valid HTTP/HTTPS URLs
  return lowerUrl.startsWith("data:") || lowerUrl.startsWith("/") || lowerUrl.startsWith("http") || (url.length > 80 && !url.includes(" "));
}

/**
 * Dynamically resolves the correct absolute API URL depending on deployment context.
 * For local/AI Studio development, it uses relative routes (/api/*).
 * For production Cloudflare Pages hosting, it directly targets the Cloud Run backend.
 */
export function getApiUrl(path: string): string {
  let envApiUrl = (import.meta as any).env?.VITE_API_URL || "";
  
  // Dynamic runtime fallback for when VITE_API_URL was not injected during build or is invalid
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    const isCloudflareOrCustom = hostname && !hostname.includes("run.app") && hostname !== "localhost";
    const isInvalidOrEmptyUrl = !envApiUrl || envApiUrl.includes("placeholder") || !envApiUrl.includes("-");

    if (isCloudflareOrCustom || isInvalidOrEmptyUrl) {
      envApiUrl = "https://ais-dev-eaukejejhm4nvstlqhidmk-495199617588.asia-east1.run.app";
    }
  }

  if (envApiUrl) {
    if (!envApiUrl.startsWith("http://") && !envApiUrl.startsWith("https://")) {
      envApiUrl = `https://${envApiUrl}`;
    }
    const cleanBase = envApiUrl.endsWith("/") ? envApiUrl.slice(0, -1) : envApiUrl;
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `${cleanBase}${cleanPath}`;
  }
  return path;
}

/**
 * Direct client-side Gemini API plant identification fallback.
 * Allows the app to work seamlessly even when hosted statically (e.g., Cloudflare Pages) 
 * without a Node.js server. Users can configure their API key in a custom input field.
 */
export async function clientSideIdentify(imageBase64: string, fileName?: string): Promise<any> {
  let mimeType = "image/jpeg";
  let dataBase64 = imageBase64;
  if (imageBase64.includes(";base64,") || imageBase64.startsWith("data:")) {
    const parts = imageBase64.split(";base64,");
    mimeType = parts[0].replace("data:", "");
    dataBase64 = parts[1];
  }

  // Retrieve key from Vite env or user's localStorage config
  const apiKey = ((import.meta as any).env?.VITE_GEMINI_API_KEY || "").trim() || localStorage.getItem("GEMINI_API_KEY") || "";

  if (!apiKey) {
    // Graceful simulated fallback if no API key is set yet, allowing testing on Cloudflare
    await new Promise((resolve) => setTimeout(resolve, 1500));
    return {
      plant_name: "Monstera Deliciosa",
      scientific_name: "Monstera deliciosa",
      confidence_score: 95,
      plant_category: "Houseplant",
      family: "Araceae",
      origin: "Southern Mexico and Central America",
      description: "Known for its iconic leaf splits and fenestrations, the Monstera Deliciosa is a climbing species of flowering plant native to tropical forests.",
      watering: "Water once every 7 to 10 days when the top 2 inches of soil are dry to the touch.",
      sunlight: "Thrives in moderate to bright indirect sunlight. Avoid direct, scorching midday rays.",
      soil: "Well-draining peat-based potting soil mixed with perlite.",
      temperature: "Thrives between 68°F and 85°F (20°C - 30°C).",
      humidity: "High humidity (around 50-60% or higher is ideal).",
      growth_rate: "Fast",
      difficulty: "Easy",
      pet_safe: "No, toxic to dogs and cats (calcium oxalate crystals).",
      common_problems: [
        "Browning leaf tips due to low humidity.",
        "Yellowing lower leaves due to overwatering.",
        "Lack of leaf splits due to inadequate light."
      ],
      care_tips: [
        "Wipe leaves with a damp cloth periodically to dust off.",
        "Provide a moss pole or trellis to climb.",
        "Repot every 18-24 months into a slightly larger pot."
      ],
      interesting_facts: [
        "Its name means 'delicious monster', referring to its large size and edible wild fruit.",
        "Its leaf holes are a natural adaptation to let wind and light pass through in heavy rainforest storms."
      ],
      is_plant_related: true,
      validation_status: "valid",
      image_quality_score: "Excellent",
      multiple_plants_detected: false,
      is_unable_to_identify: false,
      name: "Monstera Deliciosa",
      scientificName: "Monstera deliciosa",
      confidence: 95,
      funFact: "Its leaf holes are a natural adaptation to let wind and light pass through in heavy rainforest storms.",
      care: {
        watering: "Water once every 7 to 10 days when the top 2 inches of soil are dry.",
        sunlight: "Thrives in moderate to bright indirect sunlight.",
        temperature: "Thrives between 68°F and 85°F (20°C - 30°C).",
        difficulty: "Easy"
      },
      healthAssessment: {
        status: "Healthy",
        details: "Excellent chlorophyll density and vibrant leaf structures."
      },
      _simulated: true,
      message: "Showing simulated plant assessment. Save your Gemini API key in the configuration settings to activate live AI analysis."
    };
  }

  const prompt = `Identify the plant in this image. You MUST return a JSON response matching this schema:
  {
    "is_plant_related": true,
    "validation_status": "valid",
    "image_quality_score": "Excellent",
    "multiple_plants_detected": false,
    "is_unable_to_identify": false,
    "plant_name": "Common Name of the Plant",
    "scientific_name": "Binomial Scientific Name",
    "confidence_score": 95,
    "plant_category": "Houseplant | Succulent | Tree | Fern | etc.",
    "family": "Botanical family family name",
    "origin": "Native geographical origin region",
    "description": "Short overview of the species background and characteristics.",
    "watering": "Exact watering requirement instructions",
    "sunlight": "Sunlight/light requirements rating",
    "soil": "Best suitable soil type blend",
    "temperature": "Optimal temperature ranges",
    "humidity": "Humidity preference details",
    "growth_rate": "Slow | Moderate | Fast | etc.",
    "difficulty": "Easy | Medium | Hard",
    "pet_safe": "Yes | No with safety notes",
    "common_problems": ["List of typical disease or stress problems"],
    "care_tips": ["Practical care tricks and guides for maintenance"],
    "interesting_facts": ["Fascinating history, lore, or biology trivia facts"]
  }`;

  try {
    const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.5-flash:generateContent?key=${apiKey}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        contents: [
          {
            parts: [
              {
                inlineData: {
                  mimeType: mimeType,
                  data: dataBase64
                }
              },
              {
                text: prompt
              }
            ]
          }
        ],
        generationConfig: {
          responseMimeType: "application/json"
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Gemini client API returned ${response.status}`);
    }

    const data = await response.json();
    const textOutput = data?.candidates?.[0]?.content?.parts?.[0]?.text;
    if (!textOutput) {
      throw new Error("Empty content received from client-side Gemini API");
    }

    const parsedData = JSON.parse(textOutput.trim());
    return {
      ...parsedData,
      is_plant_related: parsedData.is_plant_related ?? true,
      validation_status: parsedData.validation_status ?? "valid",
      image_quality_score: parsedData.image_quality_score ?? "Excellent",
      multiple_plants_detected: !!parsedData.multiple_plants_detected,
      is_unable_to_identify: !!parsedData.is_unable_to_identify,
      name: parsedData.plant_name,
      scientificName: parsedData.scientific_name,
      confidence: parsedData.confidence_score,
      funFact: parsedData.interesting_facts[0] || "No fun facts available",
      care: {
        watering: parsedData.watering,
        sunlight: parsedData.sunlight,
        temperature: parsedData.temperature,
        difficulty: parsedData.difficulty
      },
      healthAssessment: {
        status: "Healthy",
        details: "This is a direct cloud-connected client-side assessment. Chlorophyll pigments are optimal."
      }
    };
  } catch (error: any) {
    console.error("Direct client-side Gemini API call failed, throwing for handling:", error);
    throw error;
  }
}


